import { Box, Hint } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { Features } from 'generated/featureDefinitions'
import isEmpty from 'lodash/isEmpty'
import { useLocalStorage } from 'usehooks-ts'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import Publish from 'modules/shift_management/components/ShiftManagement/Layout/Actions/Publish'
import { Separator } from 'modules/shift_management/components/ShiftManagement/Separator'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionState } from 'modules/shift_management/hooks/useSelection'

import AddShiftButton from './AddShiftButton'
import ClearSelectionButton from './ClearSelectionButton'
import CopyPeriodButton from './CopyPeriodButton'
import CopyShiftButton from './CopyShiftButton'
import DeleteButton from './DeleteButton'
import EditShiftsButton from './EditShiftButton'
import RunAutoScheduleButton from './RunAutoScheduleButton'
import ShowCoverageSectionButton from './ShowCoverageSectionButton'

interface Props {
  hasNoneShiftManagementPlanning?: boolean
  disabled?: boolean
  setShowCoverageSection: (showCoverageSection: boolean) => void
  showCoverageSection?: boolean
  hasTimeOffModule: boolean
}

const ShiftActionsBar = ({
  hasNoneShiftManagementPlanning,
  disabled,
  setShowCoverageSection,
  showCoverageSection,
  hasTimeOffModule,
}: Props) => {
  const [{ view }] = useSchedule()
  const { isEnabled: isSelectionEnabled, shifts: selectedShifts } = useSelectionState()
  const [{ canUseCoverageView, canUseCopySchedulePeriod, hasFeatureFlag, hasFeatureEntitlement }] =
    useEnabledFeatures()

  const showCoverageSectionButton = view.isByEmployees && view.isWeekly
  const shifts = Object.values(selectedShifts)
  const isAutomationEnabled = hasFeatureEntitlement(Features.shift_management_automatic_planning)

  const newEntryFlowEnabled = hasFeatureFlag('dev_shift_management_autoscheduler_entry_flow')
  const [hasSeenNewEntryFlowHints] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.shiftManagementHasSeenNewEntryFlowHints,
    false
  )

  return (
    <Box
      flexDirection="row"
      alignItems="center"
      gap="s8"
      justifyContent="flexEnd"
      style={{
        flexGrow: '1',
        flexBasis: '0px',
      }}
    >
      {showCoverageSectionButton && !newEntryFlowEnabled && (
        <Box flexDirection="row" alignItems="center" gap="s8">
          <Separator />
          <ShowCoverageSectionButton
            setShowCoverageSection={setShowCoverageSection}
            showCoverageSection={showCoverageSection}
            hasNoneShiftManagementPlanning={disabled}
            hasCoverageView={canUseCoverageView}
          />
        </Box>
      )}
      {!isSelectionEnabled && view.isWeekly && !canUseCopySchedulePeriod && (
        <CopyShiftButton disabled={hasNoneShiftManagementPlanning} />
      )}
      {isSelectionEnabled && !isEmpty(shifts) && <EditShiftsButton />}
      {isSelectionEnabled && <ClearSelectionButton />}
      {isSelectionEnabled && newEntryFlowEnabled && <Separator />}
      <AddShiftButton
        disabled={hasNoneShiftManagementPlanning}
        hasTimeOffModule={hasTimeOffModule}
      />
      {canUseCopySchedulePeriod && <CopyPeriodButton disabled={hasNoneShiftManagementPlanning} />}
      <DeleteButton disabled={hasNoneShiftManagementPlanning} />
      <Separator />
      {newEntryFlowEnabled &&
        isAutomationEnabled &&
        (!hasSeenNewEntryFlowHints ? (
          <Hint.Popover
            step={2}
            title={i18n.t('shift_management.hints.auto_schedule.title')}
            description={i18n.t('shift_management.hints.auto_schedule.description')}
            actionLabel={i18n.t('common.next')}
            position="bottom left"
          >
            <RunAutoScheduleButton />
          </Hint.Popover>
        ) : (
          <RunAutoScheduleButton />
        ))}
      <Publish />
    </Box>
  )
}

export default observer(ShiftActionsBar)
